export type SourceType = 'eurlex' | 'pdf' | 'qa' | 'concept' | 'workspace' | 'user_upload' | 'unknown';

export interface FindingSourceFields {
  source_type?: string;
  category?: string;            // "regulatory" (default) | "input"
  source_workspace_id?: string; // set when category === "input"
  source_document_name?: string;
  source_document_celex?: string;
  source_service?: string;
  source_url?: string;
  pdf_url?: string;
  page_url?: string;
  chunk_id?: string;
  page_number?: number;
}

export function isMeaningful(value: string | undefined | null): value is string {
  if (!value) return false;
  const lower = value.toLowerCase().trim();
  return lower !== '' && lower !== 'unknown' && lower !== 'unknown_document' && !lower.startsWith('unknown');
}

export function isEurLexCelex(value: string | undefined | null): value is string {
  if (!value) return false;
  return /^[0-9]\d{4}[A-Z]\d{4}(?:-\d{8})?$/.test(value.trim().toUpperCase());
}

export function getSafeSourceUrl(value: string | undefined | null): string | null {
  if (!value) return null;

  try {
    const parsed = new URL(value.trim());
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.href;
    }
  } catch {
    return null;
  }

  return null;
}

export function isPdfUrl(value: string | undefined | null): boolean {
  const safeUrl = getSafeSourceUrl(value);
  if (!safeUrl) return false;

  try {
    const parsed = new URL(safeUrl);
    return parsed.pathname.toLowerCase().endsWith('.pdf');
  } catch {
    return false;
  }
}

export function hasPdfSourceMetadata(finding: FindingSourceFields): boolean {
  return Boolean(
    getSafeSourceUrl(finding.pdf_url) ||
    finding.chunk_id ||
    typeof finding.page_number === 'number' ||
    isPdfUrl(finding.source_url) ||
    isPdfUrl(finding.page_url)
  );
}

export function detectFindingSourceType(finding: FindingSourceFields): SourceType {
  const sourceType = finding.source_type?.toLowerCase();
  // Workspace-sourced input facts (institution profile, upstream specs) —
  // "each workspace is a doc". Checked first: category is authoritative.
  if (finding.category === 'input' || sourceType === 'workspace') return 'workspace';
  if (sourceType === 'eba_qa' || sourceType === 'qa') return 'qa';
  if (sourceType === 'concept') return 'concept';
  if (sourceType === 'user_upload' || sourceType === 'uploaded_document') return 'user_upload';
  if (sourceType === 'pdf' || finding.source_service === 'pdf' || hasPdfSourceMetadata(finding)) return 'pdf';

  const docName = (finding.source_document_name || '').toLowerCase().trim();
  if (docName === 'eba_single_rulebook_qa' || /\bq\s*&?\s*a\b/.test(docName) || /\bqa\b/.test(docName)) {
    return 'qa';
  }

  const celex = finding.source_document_celex || '';
  if (isEurLexCelex(celex)) return 'eurlex';
  return 'unknown';
}
